import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import { useStyles } from './DriverSelector.styles';

/**
 * Category presets as specified by the task:
 *   All Active, All Clocked-In, By Area, Busy, Free
 *
 * Status semantics:
 *   busy / free  → in-app active
 *   soon         → clocked-in but not yet active
 *   undefined    → offline / not clocked-in
 *
 * "By Area" is a GROUPING VIEW preset, not a filter preset. When active, the
 * driver list is rendered grouped by region. Each area group gets a "Select All
 * in [Region]" button so dispatchers can select entire groups. Individual
 * checkboxes still work across all modes.
 */
const STATUS_FILTER_CATEGORIES = [
  {
    id: 'all-active',
    label: 'All Active',
    filter: (d) => d.status === 'busy' || d.status === 'free',
  },
  {
    id: 'all-clocked-in',
    label: 'All Clocked-In',
    filter: (d) =>
      d.status === 'busy' || d.status === 'free' || d.status === 'soon',
  },
  {
    id: 'busy',
    label: 'Busy',
    filter: (d) => d.status === 'busy',
  },
  {
    id: 'free',
    label: 'Free',
    filter: (d) => d.status === 'free',
  },
];

const BY_AREA_ID = 'by-area';

/** Display label for a driver status. */
function statusLabel(status) {
  if (!status) return 'offline';
  return status;
}

/**
 * Groups a driver list by `location.region` when available.
 * If no driver has a region, returns null to signal flat-list fallback.
 * Returns an array of { region: string, drivers: [] } sorted alphabetically.
 */
function groupByRegion(drivers) {
  const hasRegion = drivers.some((d) => d.location?.region);
  if (!hasRegion) return null;

  const map = {};
  drivers.forEach((d) => {
    const region = d.location?.region ?? 'Other';
    if (!map[region]) map[region] = [];
    map[region].push(d);
  });
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, driverList]) => ({ region, drivers: driverList }));
}

/**
 * Reusable DriverSelector component.
 *
 * Props:
 *  - drivers:   Full roster array from useDriverRoster.
 *               Shape: { id, firstName, lastName, status?, location? }
 *  - selected:  Set<string> of selected driver IDs.
 *  - onChange:  (newSelectedSet: Set<string>) => void
 *  - isLoading: bool
 */
export function DriverSelector({ drivers, selected, onChange, isLoading }) {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const [activeStatusCats, setActiveStatusCats] = useState(new Set());
  const [areaMode, setAreaMode] = useState(false);

  /**
   * Drivers after applying active status-category filters.
   * If no status category is active, all roster drivers pass through.
   */
  const categoryFilteredDrivers = useMemo(() => {
    const active = STATUS_FILTER_CATEGORIES.filter((c) =>
      activeStatusCats.has(c.id)
    );
    if (active.length === 0) return drivers ?? [];
    return (drivers ?? []).filter((d) => active.some((c) => c.filter(d)));
  }, [drivers, activeStatusCats]);

  /**
   * Drivers after applying name search on top of category filter.
   * This is the final flat list shown when not in area mode.
   */
  const visibleDrivers = useMemo(() => {
    if (!search.trim()) return categoryFilteredDrivers;
    const q = search.toLowerCase();
    return categoryFilteredDrivers.filter(
      (d) =>
        d.firstName?.toLowerCase().includes(q) ||
        d.lastName?.toLowerCase().includes(q)
    );
  }, [categoryFilteredDrivers, search]);

  /**
   * Area groups. null = fallback to flat list even in area mode.
   */
  const areaGroups = useMemo(
    () => (areaMode ? groupByRegion(visibleDrivers) : null),
    [areaMode, visibleDrivers]
  );

  const toggleStatusCategory = (catId) => {
    const cat = STATUS_FILTER_CATEGORIES.find((c) => c.id === catId);
    if (!cat) return;

    const newActiveCats = new Set(activeStatusCats);
    const wasActive = newActiveCats.has(catId);

    if (wasActive) {
      newActiveCats.delete(catId);
    } else {
      newActiveCats.add(catId);
    }
    setActiveStatusCats(newActiveCats);

    const newSelected = new Set(selected);
    const driversInCat = (drivers ?? []).filter(cat.filter);

    if (!wasActive) {
      driversInCat.forEach((d) => newSelected.add(String(d.id)));
    } else {
      driversInCat.forEach((d) => {
        const stillCovered = [...newActiveCats].some((id) => {
          const other = STATUS_FILTER_CATEGORIES.find((c) => c.id === id);
          return other ? other.filter(d) : false;
        });
        if (!stillCovered) newSelected.delete(String(d.id));
      });
    }
    onChange(newSelected);
  };

  const toggleByArea = () => {
    setAreaMode((prev) => !prev);
  };

  const toggleDriver = (driverId) => {
    const id = String(driverId);
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onChange(newSelected);
  };

  /**
   * Selects all drivers in a given area group (used by the area-group button).
   * Drivers already selected stay selected.
   */
  const selectAreaGroup = (groupDrivers) => {
    const newSelected = new Set(selected);
    groupDrivers.forEach((d) => newSelected.add(String(d.id)));
    onChange(newSelected);
  };

  const renderDriverRow = (driver) => {
    const id = String(driver.id);
    const isChecked = selected.has(id);
    return (
      <Box
        key={id}
        className={classes.driverItem}
        onClick={() => toggleDriver(id)}
        role="checkbox"
        aria-checked={isChecked}
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter') toggleDriver(id);
        }}
      >
        <Checkbox
          size="small"
          checked={isChecked}
          onChange={() => toggleDriver(id)}
          onClick={(e) => e.stopPropagation()}
          color="primary"
        />
        <Typography className={classes.driverName}>
          {driver.firstName} {driver.lastName}
        </Typography>
        <Chip
          className={classes.statusChip}
          label={statusLabel(driver.status)}
          size="small"
          color={
            driver.status === 'busy' || driver.status === 'free'
              ? 'primary'
              : 'default'
          }
        />
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const renderDriverList = () => {
    if (visibleDrivers.length === 0) {
      return (
        <Typography className={classes.emptyState}>
          No drivers match your search or filter.
        </Typography>
      );
    }

    if (areaMode && areaGroups !== null) {
      return areaGroups.map(({ region, drivers: groupDrivers }) => (
        <Box key={region}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            className={classes.areaGroupHeader}
          >
            <Typography className={classes.areaGroupLabel}>
              {region} ({groupDrivers.length})
            </Typography>
            <Button
              size="small"
              color="primary"
              onClick={() => selectAreaGroup(groupDrivers)}
              className={classes.selectAreaBtn}
            >
              Select all
            </Button>
          </Box>
          <Divider />
          {groupDrivers.map(renderDriverRow)}
        </Box>
      ));
    }

    return visibleDrivers.map(renderDriverRow);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.chipRow}>
        {STATUS_FILTER_CATEGORIES.map((cat) => (
          <Chip
            key={cat.id}
            label={cat.label}
            size="small"
            clickable
            color={activeStatusCats.has(cat.id) ? 'primary' : 'default'}
            variant={activeStatusCats.has(cat.id) ? 'outlined' : 'default'}
            onClick={() => toggleStatusCategory(cat.id)}
          />
        ))}
        <Chip
          key={BY_AREA_ID}
          label="By Area"
          size="small"
          clickable
          color={areaMode ? 'primary' : 'default'}
          variant={areaMode ? 'outlined' : 'default'}
          onClick={toggleByArea}
        />
      </Box>

      <TextField
        className={classes.searchBox}
        variant="outlined"
        size="small"
        placeholder="Search by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
      />

      <Typography className={classes.selectedCount}>
        {selected.size > 0
          ? `${selected.size} driver${selected.size !== 1 ? 's' : ''} selected`
          : 'No drivers selected'}
      </Typography>

      <Box className={classes.driverList}>{renderDriverList()}</Box>
    </Box>
  );
}

DriverSelector.propTypes = {
  drivers: PropTypes.array,
  selected: PropTypes.instanceOf(Set).isRequired,
  onChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

DriverSelector.defaultProps = {
  drivers: [],
  isLoading: false,
};
